/**
 * Background Controller
 * Handles canvas background rendering with support for colors, gradients, patterns, and images
 */

import { backgroundState, managers } from './state.js';
import { showNotification } from '../lib/notification-utils.js';

// Cache for loaded background image
let cachedBackgroundImage = null;
let cachedImageUrl = null;

// Pre-rendered pattern canvases for performance
const patternCache = new Map();

// Version counter for background changes (used by 3D visualizers to know when to update)
let backgroundVersion = 0;

// Cached background canvas for 3D texture rendering
let backgroundTextureCanvas = null;
let backgroundTextureVersion = -1;

/**
 * Initialize background controller and set up event listeners
 */
export function initBackgroundController() {
  setupBackgroundTypeSelector();
  setupColorControls();
  setupGradientControls();
  setupPatternControls();
  setupImageControls();
  loadBackgroundSettings();

  console.log('[BackgroundController] Initialized');
}

/**
 * Load saved background settings from localStorage
 */
function loadBackgroundSettings() {
  const saved = localStorage.getItem('visualizer_background');
  if (saved) {
    try {
      const settings = JSON.parse(saved);
      Object.assign(backgroundState, settings);

      // Don't restore image URL from localStorage (too large, use IndexedDB for real persistence)
      if (settings.imageUrl) {
        backgroundState.imageUrl = null;
      }

      // Ensure pattern values are valid (in case of corrupted/old localStorage data)
      if (typeof backgroundState.patternOpacity !== 'number' || isNaN(backgroundState.patternOpacity)) {
        backgroundState.patternOpacity = 0.1;
      }
      if (typeof backgroundState.patternScale !== 'number' || isNaN(backgroundState.patternScale) || backgroundState.patternScale <= 0) {
        backgroundState.patternScale = 1;
      }
      if (typeof backgroundState.patternSize !== 'number' || isNaN(backgroundState.patternSize) || backgroundState.patternSize <= 0) {
        backgroundState.patternSize = 2;
      }

      updateUIFromState();
    } catch (e) {
      console.warn('[BackgroundController] Failed to load saved settings:', e);
    }
  }
}

/**
 * Save current background settings to localStorage
 */
function saveBackgroundSettings() {
  const toSave = { ...backgroundState };
  // Don't save image URL to localStorage (too large)
  toSave.imageUrl = null;
  localStorage.setItem('visualizer_background', JSON.stringify(toSave));
}

/**
 * Update UI controls to reflect current state
 */
function updateUIFromState() {
  // Update type selector
  const typeSelect = document.getElementById('bgType');
  if (typeSelect) typeSelect.value = backgroundState.type;

  // Update all color inputs that use the base color
  const colorInputs = ['bgColor', 'bgGradientColor1', 'bgPatternBaseColor', 'bgImageFallbackColor'];
  colorInputs.forEach(id => {
    const input = document.getElementById(id);
    if (input) input.value = backgroundState.color;
  });

  const color2Input = document.getElementById('bgColor2');
  if (color2Input) color2Input.value = backgroundState.color2;

  // Update gradient direction
  const gradientDir = document.getElementById('bgGradientDirection');
  if (gradientDir) gradientDir.value = backgroundState.gradientDirection;

  // Update pattern controls
  const patternSelect = document.getElementById('bgPattern');
  if (patternSelect) patternSelect.value = backgroundState.pattern;

  const patternColorInput = document.getElementById('bgPatternColor');
  if (patternColorInput) patternColorInput.value = backgroundState.patternColor;

  const patternOpacity = document.getElementById('bgPatternOpacity');
  if (patternOpacity) {
    patternOpacity.value = backgroundState.patternOpacity * 100;
    const valueEl = document.getElementById('bgPatternOpacityValue');
    if (valueEl) valueEl.textContent = Math.round(backgroundState.patternOpacity * 100) + '%';
  }

  const patternScale = document.getElementById('bgPatternScale');
  if (patternScale) {
    patternScale.value = backgroundState.patternScale * 100;
    const valueEl = document.getElementById('bgPatternScaleValue');
    if (valueEl) valueEl.textContent = backgroundState.patternScale.toFixed(1) + 'x';
  }

  const patternSize = document.getElementById('bgPatternSize');
  if (patternSize) {
    patternSize.value = backgroundState.patternSize;
    const valueEl = document.getElementById('bgPatternSizeValue');
    if (valueEl) valueEl.textContent = backgroundState.patternSize + 'px';
  }

  // Update image fit
  const imageFit = document.getElementById('bgImageFit');
  if (imageFit) imageFit.value = backgroundState.imageFit;

  // Update visibility of control groups
  updateControlVisibility();
}

/**
 * Update which control groups are visible based on background type
 */
function updateControlVisibility() {
  const type = backgroundState.type;

  const solidControls = document.getElementById('bgSolidControls');
  const gradientControls = document.getElementById('bgGradientControls');
  const patternControls = document.getElementById('bgPatternControls');
  const imageControls = document.getElementById('bgImageControls');

  if (solidControls) solidControls.style.display = type === 'solid' ? 'block' : 'none';
  if (gradientControls) gradientControls.style.display = type === 'gradient' ? 'block' : 'none';
  if (patternControls) patternControls.style.display = type === 'pattern' ? 'block' : 'none';
  if (imageControls) imageControls.style.display = type === 'image' ? 'block' : 'none';

  // Update image preview
  updateImagePreview();
}

/**
 * Set up background type selector
 */
function setupBackgroundTypeSelector() {
  const typeSelect = document.getElementById('bgType');
  if (!typeSelect) return;

  typeSelect.addEventListener('change', (e) => {
    backgroundState.type = e.target.value;
    updateControlVisibility();
    saveBackgroundSettings();
    requestRedraw();
  });
}

/**
 * Set up solid color controls
 */
function setupColorControls() {
  // All color inputs that control the base/primary color
  const colorInputIds = ['bgColor', 'bgGradientColor1', 'bgPatternBaseColor', 'bgImageFallbackColor'];

  colorInputIds.forEach(id => {
    const colorInput = document.getElementById(id);
    if (colorInput) {
      colorInput.addEventListener('input', (e) => {
        backgroundState.color = e.target.value;
        // Sync all color inputs to the same value
        colorInputIds.forEach(syncId => {
          const syncInput = document.getElementById(syncId);
          if (syncInput && syncInput !== e.target) {
            syncInput.value = e.target.value;
          }
        });
        saveBackgroundSettings();
        requestRedraw();
      });
    }
  });
}

/**
 * Set up gradient controls
 */
function setupGradientControls() {
  const color2Input = document.getElementById('bgColor2');
  if (color2Input) {
    color2Input.addEventListener('input', (e) => {
      backgroundState.color2 = e.target.value;
      saveBackgroundSettings();
      requestRedraw();
    });
  }

  const gradientDir = document.getElementById('bgGradientDirection');
  if (gradientDir) {
    gradientDir.addEventListener('change', (e) => {
      backgroundState.gradientDirection = e.target.value;
      saveBackgroundSettings();
      requestRedraw();
    });
  }
}

/**
 * Set up pattern controls
 */
function setupPatternControls() {
  const patternSelect = document.getElementById('bgPattern');
  if (patternSelect) {
    patternSelect.addEventListener('change', (e) => {
      backgroundState.pattern = e.target.value;
      patternCache.clear(); // Clear pattern cache when pattern changes
      saveBackgroundSettings();
      requestRedraw();
    });
  }

  const patternColorInput = document.getElementById('bgPatternColor');
  if (patternColorInput) {
    patternColorInput.addEventListener('input', (e) => {
      backgroundState.patternColor = e.target.value;
      patternCache.clear();
      saveBackgroundSettings();
      requestRedraw();
    });
  }

  const patternOpacity = document.getElementById('bgPatternOpacity');
  if (patternOpacity) {
    patternOpacity.addEventListener('input', (e) => {
      backgroundState.patternOpacity = parseInt(e.target.value) / 100;
      const valueEl = document.getElementById('bgPatternOpacityValue');
      if (valueEl) valueEl.textContent = e.target.value + '%';
      patternCache.clear();
      saveBackgroundSettings();
      requestRedraw();
    });
  }

  const patternScale = document.getElementById('bgPatternScale');
  if (patternScale) {
    patternScale.addEventListener('input', (e) => {
      backgroundState.patternScale = parseInt(e.target.value) / 100;
      const valueEl = document.getElementById('bgPatternScaleValue');
      if (valueEl) valueEl.textContent = backgroundState.patternScale.toFixed(1) + 'x';
      patternCache.clear();
      saveBackgroundSettings();
      requestRedraw();
    });
  }

  const patternSize = document.getElementById('bgPatternSize');
  if (patternSize) {
    patternSize.addEventListener('input', (e) => {
      backgroundState.patternSize = parseInt(e.target.value);
      const valueEl = document.getElementById('bgPatternSizeValue');
      if (valueEl) valueEl.textContent = e.target.value + 'px';
      patternCache.clear();
      saveBackgroundSettings();
      requestRedraw();
    });
  }
}

/**
 * Set up image upload controls with drag-and-drop support
 */
function setupImageControls() {
  const imageInput = document.getElementById('bgImageInput');
  const uploadBtn = document.getElementById('bgUploadBtn');
  const clearBtn = document.getElementById('bgClearImageBtn');
  const imageFit = document.getElementById('bgImageFit');
  const dropzone = document.getElementById('bgImageDropzone');

  // Handle file selection via button
  if (uploadBtn && imageInput) {
    uploadBtn.addEventListener('click', (e) => {
      e.stopPropagation(); // Prevent dropzone click
      imageInput.click();
    });

    imageInput.addEventListener('change', (e) => {
      const file = e.target.files[0];
      if (file) {
        handleImageFile(file);
      }
      imageInput.value = ''; // Reset for future selections
    });
  }

  // Set up dropzone drag-and-drop
  if (dropzone) {
    // Click anywhere on dropzone to browse
    dropzone.addEventListener('click', (e) => {
      if (e.target === dropzone || e.target.tagName === 'SPAN' || e.target.tagName === 'svg' || e.target.tagName === 'I') {
        imageInput?.click();
      }
    });

    dropzone.addEventListener('dragover', (e) => {
      e.preventDefault();
      e.stopPropagation();
      dropzone.classList.add('drag-over');
    });

    dropzone.addEventListener('dragleave', (e) => {
      e.preventDefault();
      e.stopPropagation();
      dropzone.classList.remove('drag-over');
    });

    dropzone.addEventListener('drop', (e) => {
      e.preventDefault();
      e.stopPropagation();
      dropzone.classList.remove('drag-over');

      const files = e.dataTransfer.files;
      if (files.length > 0) {
        // Find first image file
        const imageFile = Array.from(files).find(f => f.type.startsWith('image/'));
        if (imageFile) {
          handleImageFile(imageFile);
        }
      }
    });
  }

  // Handle clear/remove button
  if (clearBtn) {
    clearBtn.addEventListener('click', () => {
      backgroundState.imageUrl = null;
      cachedBackgroundImage = null;
      cachedImageUrl = null;
      updateImagePreview();
      saveBackgroundSettings();
      requestRedraw();
    });
  }

  // Handle fit mode change
  if (imageFit) {
    imageFit.addEventListener('change', (e) => {
      backgroundState.imageFit = e.target.value;
      saveBackgroundSettings();
      requestRedraw();
    });
  }
}

const MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 10 MB

/**
 * Handle an image file (from input or drag-drop)
 */
function handleImageFile(file) {
  if (!file || !file.type.startsWith('image/')) {
    console.warn('[BackgroundController] Invalid file type:', file?.type);
    return;
  }

  if (file.size > MAX_IMAGE_SIZE) {
    const sizeMB = (file.size / 1024 / 1024).toFixed(1);
    console.warn(`[BackgroundController] Image too large: ${sizeMB}MB (limit ${MAX_IMAGE_SIZE / 1024 / 1024}MB)`);
    showNotification(`Image too large (${sizeMB} MB). Maximum size is 10 MB.`, { type: 'warning' });
    return;
  }

  const reader = new FileReader();
  reader.onload = (event) => {
    backgroundState.imageUrl = event.target.result;
    cachedBackgroundImage = null; // Clear cache to reload
    cachedImageUrl = null;
    updateImagePreview();
    saveBackgroundSettings();
    requestRedraw();
  };
  reader.readAsDataURL(file);
}

/**
 * Update image preview - toggle between dropzone and preview
 */
function updateImagePreview() {
  const dropzone = document.getElementById('bgImageDropzone');
  const previewContainer = document.getElementById('bgImagePreviewContainer');
  const preview = document.getElementById('bgImagePreview');

  if (backgroundState.imageUrl) {
    // Show preview, hide dropzone
    if (dropzone) dropzone.style.display = 'none';
    if (previewContainer) previewContainer.style.display = 'block';
    if (preview) preview.src = backgroundState.imageUrl;

    // Re-initialize lucide icons for the trash button
    if (window.lucide) {
      window.lucide.createIcons();
    }
  } else {
    // Show dropzone, hide preview
    if (dropzone) dropzone.style.display = 'flex';
    if (previewContainer) previewContainer.style.display = 'none';
    if (preview) preview.src = '';

    // Re-initialize lucide icons for the dropzone
    if (window.lucide) {
      window.lucide.createIcons();
    }
  }
}

/**
 * Request a redraw of the visualizer
 */
function requestRedraw() {
  backgroundVersion++;
  if (managers.visualizerManager) {
    managers.visualizerManager.clear();
  }
}

/**
 * Draw the background on a canvas context
 * Called by visualizers before rendering their content
 * @param {CanvasRenderingContext2D} ctx - Canvas context
 * @param {number} width - Canvas width
 * @param {number} height - Canvas height
 */
export function drawBackground(ctx, width, height) {
  const state = backgroundState;

  switch (state.type) {
    case 'solid':
      drawSolidBackground(ctx, width, height);
      break;
    case 'gradient':
      drawGradientBackground(ctx, width, height);
      break;
    case 'pattern':
      drawPatternBackground(ctx, width, height);
      break;
    case 'image':
      drawImageBackground(ctx, width, height);
      break;
    default:
      drawSolidBackground(ctx, width, height);
  }
}

/**
 * Draw solid color background
 */
function drawSolidBackground(ctx, width, height) {
  ctx.fillStyle = backgroundState.color;
  ctx.fillRect(0, 0, width, height);
}

/**
 * Draw gradient background
 */
function drawGradientBackground(ctx, width, height) {
  let gradient;

  switch (backgroundState.gradientDirection) {
    case 'horizontal':
      gradient = ctx.createLinearGradient(0, 0, width, 0);
      break;
    case 'diagonal':
      gradient = ctx.createLinearGradient(0, 0, width, height);
      break;
    case 'radial':
      gradient = ctx.createRadialGradient(
        width / 2, height / 2, 0,
        width / 2, height / 2, Math.max(width, height) / 2
      );
      break;
    case 'vertical':
    default:
      gradient = ctx.createLinearGradient(0, 0, 0, height);
  }

  gradient.addColorStop(0, backgroundState.color);
  gradient.addColorStop(1, backgroundState.color2);

  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, width, height);
}

/**
 * Draw pattern background
 */
function drawPatternBackground(ctx, width, height) {
  // First draw base color
  ctx.fillStyle = backgroundState.color;
  ctx.fillRect(0, 0, width, height);

  if (backgroundState.pattern === 'none') return;

  // Get or create pattern canvas
  const cacheKey = `${backgroundState.pattern}_${backgroundState.patternColor}_${backgroundState.patternOpacity}_${backgroundState.patternScale}_${backgroundState.patternSize}`;
  let patternCanvas = patternCache.get(cacheKey);

  if (!patternCanvas) {
    patternCanvas = createPatternCanvas(
      backgroundState.pattern,
      backgroundState.patternColor,
      backgroundState.patternOpacity,
      backgroundState.patternScale,
      backgroundState.patternSize
    );
    patternCache.set(cacheKey, patternCanvas);
  }

  // Create repeating pattern
  const pattern = ctx.createPattern(patternCanvas, 'repeat');
  if (pattern) {
    ctx.fillStyle = pattern;
    ctx.fillRect(0, 0, width, height);
  }
}

/**
 * Create a pattern tile canvas
 * @param {string} patternType - Pattern type (dots, grid, lines, waves)
 * @param {string} color - Pattern color
 * @param {number} opacity - Pattern opacity (0-1)
 * @param {number} scale - Pattern scale multiplier (affects tile size/spacing)
 * @param {number} size - Element size in pixels (line width, dot diameter)
 */
function createPatternCanvas(patternType, color, opacity, scale, size) {
  // Ensure valid values with sensible defaults
  const safeScale = (typeof scale === 'number' && !isNaN(scale) && scale > 0) ? scale : 1;
  const safeOpacity = (typeof opacity === 'number' && !isNaN(opacity)) ? Math.max(0, Math.min(1, opacity)) : 0.1;
  const safeSize = (typeof size === 'number' && !isNaN(size) && size > 0) ? size : 2;

  const baseSize = Math.max(8, Math.round(32 * safeScale)); // Minimum 8px tile
  const canvas = document.createElement('canvas');
  canvas.width = baseSize;
  canvas.height = baseSize;
  const ctx = canvas.getContext('2d');

  ctx.globalAlpha = safeOpacity;
  ctx.strokeStyle = color || '#ffffff';
  ctx.fillStyle = color || '#ffffff';
  ctx.lineWidth = safeSize; // Use size parameter for line width

  switch (patternType) {
    case 'dots':
      const dotRadius = safeSize / 2; // Size is diameter, so radius is half
      ctx.beginPath();
      ctx.arc(baseSize / 2, baseSize / 2, Math.max(1, dotRadius), 0, Math.PI * 2);
      ctx.fill();
      break;

    case 'grid':
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.lineTo(baseSize, 0);
      ctx.moveTo(0, 0);
      ctx.lineTo(0, baseSize);
      ctx.stroke();
      break;

    case 'lines':
      ctx.beginPath();
      ctx.moveTo(0, baseSize);
      ctx.lineTo(baseSize, 0);
      ctx.stroke();
      break;

    case 'waves':
      ctx.beginPath();
      for (let x = 0; x <= baseSize; x += 2) {
        const y = baseSize / 2 + Math.sin(x / baseSize * Math.PI * 2) * (baseSize / 4);
        if (x === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.stroke();
      break;
  }

  return canvas;
}

/**
 * Draw image background
 */
function drawImageBackground(ctx, width, height) {
  if (!backgroundState.imageUrl) {
    drawSolidBackground(ctx, width, height);
    return;
  }

  // Load image if not cached
  if (!cachedBackgroundImage || cachedImageUrl !== backgroundState.imageUrl) {
    cachedBackgroundImage = new Image();
    cachedBackgroundImage.src = backgroundState.imageUrl;
    cachedImageUrl = backgroundState.imageUrl;

    // For now, draw solid while loading
    if (!cachedBackgroundImage.complete) {
      drawSolidBackground(ctx, width, height);
      cachedBackgroundImage.onload = () => requestRedraw();
      return;
    }
  }

  if (!cachedBackgroundImage.complete) {
    drawSolidBackground(ctx, width, height);
    return;
  }

  const img = cachedBackgroundImage;
  const imgWidth = img.naturalWidth;
  const imgHeight = img.naturalHeight;

  switch (backgroundState.imageFit) {
    case 'cover':
      drawImageCover(ctx, img, width, height, imgWidth, imgHeight);
      break;
    case 'contain':
      // Fill with color first, then draw contained image
      ctx.fillStyle = backgroundState.color;
      ctx.fillRect(0, 0, width, height);
      drawImageContain(ctx, img, width, height, imgWidth, imgHeight);
      break;
    case 'stretch':
      ctx.drawImage(img, 0, 0, width, height);
      break;
    case 'tile':
      drawImageTiled(ctx, img, width, height, imgWidth, imgHeight);
      break;
    default:
      drawImageCover(ctx, img, width, height, imgWidth, imgHeight);
  }
}

/**
 * Draw image with cover fit (fills container, may crop)
 */
function drawImageCover(ctx, img, width, height, imgWidth, imgHeight) {
  const canvasRatio = width / height;
  const imgRatio = imgWidth / imgHeight;

  let drawWidth, drawHeight, offsetX, offsetY;

  if (imgRatio > canvasRatio) {
    // Image is wider - fit by height
    drawHeight = height;
    drawWidth = imgWidth * (height / imgHeight);
    offsetX = (width - drawWidth) / 2;
    offsetY = 0;
  } else {
    // Image is taller - fit by width
    drawWidth = width;
    drawHeight = imgHeight * (width / imgWidth);
    offsetX = 0;
    offsetY = (height - drawHeight) / 2;
  }

  ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);
}

/**
 * Draw image with contain fit (shows full image, may have borders)
 */
function drawImageContain(ctx, img, width, height, imgWidth, imgHeight) {
  const canvasRatio = width / height;
  const imgRatio = imgWidth / imgHeight;

  let drawWidth, drawHeight, offsetX, offsetY;

  if (imgRatio > canvasRatio) {
    // Image is wider - fit by width
    drawWidth = width;
    drawHeight = imgHeight * (width / imgWidth);
    offsetX = 0;
    offsetY = (height - drawHeight) / 2;
  } else {
    // Image is taller - fit by height
    drawHeight = height;
    drawWidth = imgWidth * (height / imgHeight);
    offsetX = (width - drawWidth) / 2;
    offsetY = 0;
  }

  ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);
}

/**
 * Draw image tiled
 */
function drawImageTiled(ctx, img, width, height, imgWidth, imgHeight) {
  for (let x = 0; x < width; x += imgWidth) {
    for (let y = 0; y < height; y += imgHeight) {
      ctx.drawImage(img, x, y, imgWidth, imgHeight);
    }
  }
}

/**
 * Get the current background color (for visualizers that need it)
 */
export function getBackgroundColor() {
  return backgroundState.color;
}

/**
 * Get the background state (for export or persistence)
 */
export function getBackgroundState() {
  return { ...backgroundState };
}

/**
 * Set background state (for loading presets)
 */
export function setBackgroundState(newState) {
  Object.assign(backgroundState, newState);
  updateUIFromState();
  patternCache.clear();
  cachedBackgroundImage = null;
  cachedImageUrl = null;
  saveBackgroundSettings();
  requestRedraw();
}

/**
 * Get current background version (increments on each change)
 * Used by 3D visualizers to know when to update their background texture
 */
export function getBackgroundVersion() {
  return backgroundVersion;
}

/**
 * Check if background is a simple solid color
 * Used by 3D visualizers to optimize - can use THREE.Color directly for solid backgrounds
 */
export function isSimpleBackground() {
  return backgroundState.type === 'solid';
}

/**
 * Render current background to a canvas for use as a texture
 * @param {number} width - Target width
 * @param {number} height - Target height
 * @returns {HTMLCanvasElement} Canvas with rendered background
 */
export function renderBackgroundToCanvas(width, height) {
  // Reuse canvas if dimensions match and background hasn't changed
  if (backgroundTextureCanvas &&
      backgroundTextureCanvas.width === width &&
      backgroundTextureCanvas.height === height &&
      backgroundTextureVersion === backgroundVersion) {
    return backgroundTextureCanvas;
  }

  // Create or resize canvas
  if (!backgroundTextureCanvas) {
    backgroundTextureCanvas = document.createElement('canvas');
  }
  backgroundTextureCanvas.width = width;
  backgroundTextureCanvas.height = height;

  const ctx = backgroundTextureCanvas.getContext('2d');
  drawBackground(ctx, width, height);

  backgroundTextureVersion = backgroundVersion;
  return backgroundTextureCanvas;
}
